import { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [charCount, setCharCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'message') {
      setCharCount(value.length);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setCharCount(0);
      
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 3000);
    }, 1500);
  };

  return (
    <section id="contact" className="relative py-24 lg:py-32 bg-[#000000]">
      <div className="absolute left-0 top-1/4 w-96 h-96 bg-[#b89b9b]/8 rounded-full blur-[150px]"></div>
      <div className="absolute right-0 bottom-1/4 w-1/2 h-1/2 opacity-3">
        <svg className="w-full h-full" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="contact-grid" width="10" height="10" patternUnits="userSpaceOnUse">
              <circle cx="5" cy="5" r="1" fill="#c9a96e" opacity="0.3"/>
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#contact-grid)" />
        </svg>
      </div>

      <div className="relative z-10 px-6 lg:px-12">
        <div className="grid lg:grid-cols-9 gap-12 lg:gap-16">
          <div className="lg:col-span-4 space-y-8">
            <div>
              <h2 className="text-4xl lg:text-6xl font-thin text-[#f5f5f0] leading-tight mb-2">
                Let's Create
              </h2>
              <h2 className="text-5xl lg:text-7xl font-bold bg-gradient-to-r from-[#c9a96e] to-[#d4c5b9] bg-clip-text text-transparent">
                Together
              </h2>
            </div>

            <p className="text-base leading-relaxed text-[#f5f5f0]/70">
              新しいプロジェクトのご相談、デザインに関するお問い合わせ、採用についてなど、どんなことでもお気軽にご連絡ください。あなたのビジョンを実現するために、最適なソリューションをご提案いたします。通常、24時間以内にご返信させていただきます。
            </p>

            <div className="space-y-6 pt-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#c9a96e]/10 rounded-xl flex-shrink-0">
                  <i className="ri-mail-line text-xl text-[#c9a96e]"></i>
                </div>
                <div>
                  <div className="text-xs text-[#d4c5b9]/60 mb-1">Email</div>
                  <a
                    href="mailto:hello@kenjitanaka.design"
                    className="text-lg text-[#d4c5b9] hover:text-[#c9a96e] transition-colors duration-300 cursor-pointer"
                  >
                    hello@kenjitanaka.design
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#c9a96e]/10 rounded-xl flex-shrink-0">
                  <i className="ri-phone-line text-xl text-[#c9a96e]"></i>
                </div>
                <div>
                  <div className="text-xs text-[#d4c5b9]/60 mb-1">Phone</div>
                  <a
                    href="tel:+81-90-1234-5678"
                    className="text-lg text-[#d4c5b9] hover:text-[#c9a96e] transition-colors duration-300 cursor-pointer"
                  >
                    +81 90-1234-5678
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#c9a96e]/10 rounded-xl flex-shrink-0">
                  <i className="ri-map-pin-line text-xl text-[#c9a96e]"></i>
                </div>
                <div>
                  <div className="text-xs text-[#d4c5b9]/60 mb-1">Location</div>
                  <p className="text-lg text-[#d4c5b9]">
                    東京都渋谷区神宮前3-25-18<br />
                    THE SHARE 3F
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-8">
              <div className="text-xs text-[#d4c5b9]/60 mb-4">Follow Me</div>
              <div className="flex gap-3">
                {[
                  { icon: 'ri-twitter-x-line', url: '#' },
                  { icon: 'ri-instagram-line', url: '#' },
                  { icon: 'ri-linkedin-line', url: '#' },
                  { icon: 'ri-dribbble-line', url: '#' },
                  { icon: 'ri-behance-line', url: '#' }
                ].map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    className="w-12 h-12 flex items-center justify-center bg-[#c9a96e] text-[#1a1a1a] rounded-full hover:bg-[#d4c5b9] hover:rotate-12 transition-all duration-300 cursor-pointer"
                  >
                    <i className={`${social.icon} text-xl`}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-[#1a1a1a]/90 backdrop-blur-sm rounded-3xl p-8 lg:p-10 border border-[#c9a96e]/20">
              <h3 className="text-2xl font-semibold text-[#d4c5b9] mb-8">Send a Message</h3>

              <form onSubmit={handleSubmit} className="space-y-7">
                <div>
                  <label className="block text-xs text-[#d4c5b9]/70 mb-2 tracking-wider">
                    お名前 *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b-2 border-[#c9a96e]/30 text-[#f5f5f0] py-3 focus:border-[#c9a96e] focus:outline-none transition-colors duration-300"
                    placeholder="田中 健二"
                  />
                </div>

                <div>
                  <label className="block text-xs text-[#d4c5b9]/70 mb-2 tracking-wider">
                    メールアドレス *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b-2 border-[#c9a96e]/30 text-[#f5f5f0] py-3 focus:border-[#c9a96e] focus:outline-none transition-colors duration-300"
                    placeholder="hello@example.com"
                  />
                </div>

                <div>
                  <label className="block text-xs text-[#d4c5b9]/70 mb-2 tracking-wider">
                    件名 *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b-2 border-[#c9a96e]/30 text-[#f5f5f0] py-3 focus:border-[#c9a96e] focus:outline-none transition-colors duration-300"
                    placeholder="プロジェクトのご相談"
                  />
                </div>

                <div>
                  <label className="block text-xs text-[#d4c5b9]/70 mb-2 tracking-wider">
                    メッセージ *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    maxLength={500}
                    className="w-full bg-transparent border-b-2 border-[#c9a96e]/30 text-[#f5f5f0] py-3 focus:border-[#c9a96e] focus:outline-none transition-colors duration-300 resize-none"
                    placeholder="プロジェクトの詳細をお聞かせください..."
                  ></textarea>
                  <div className="text-xs text-[#d4c5b9]/50 text-right mt-2">
                    {charCount} / 500
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-14 bg-[#c9a96e] text-[#1a1a1a] rounded-xl font-semibold hover:bg-[#d4c5b9] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? '送信中...' : '送信する'}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-[#7ed6b8]/10 border border-[#7ed6b8]/30 rounded-xl text-[#7ed6b8] text-sm text-center">
                    メッセージを送信しました。ご連絡ありがとうございます！
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}